import { useCallback, useEffect, useMemo, useState } from 'react';

import TextInput from '../../../../../../general-ui/text-input/text';
import TagSelectInput from '../../../../../../general-ui/tag-select/tag-select';
import { GetChannelTracks } from '../../../../../../../connection/create-event';
import ModalComponent from '../../../../../../general-ui/modal/modal';
import { useAppDispatch, useTypedSelector } from '../../../../../../../store/reducers/use-typed-selector';

import './assign-tracks.scss';
import { updateSessionsTracks } from '../../../../../../../store/actions/admin/create-event';
import { IUpdateSessionsTracks, Session, SessionTypesEnum } from '../../../../../../../types/working-model';

interface IAssignTracksProps {
	isOpen?: (open: boolean) => void;
}

const AssignTracks = ({ isOpen }: IAssignTracksProps): JSX.Element | null => {

	const eventUUID = useTypedSelector(state => state.CreateEventReducer.workingEvent?.uuid);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const activeChannel = useTypedSelector(state => state.AuthReducer.user?.active_channel);
	const allSessions = useTypedSelector(state => state.CreateEventReducer.workingEvent?.sessions);

	const [assignTracksEdits, setAssignTracksEdits] = useState<{ [sessionUUID: string]: IUpdateSessionsTracks }>({});
	const [showModal, setShowModal] = useState(false);
	const [trackDropdownList, setTrackDropdownList] = useState<string[]>([]);

	const dispatch = useAppDispatch();

	const { onDemandSessions } = useMemo(() => {
		const onDemandSessions: Session[] = [];
		allSessions?.forEach(session => {
			if (session.session_type === SessionTypesEnum.onDemand) {
				onDemandSessions.push(session);
			}
		});
		return { onDemandSessions };
	}, [allSessions]);

	// Sets initial state for each editable session
	useEffect(() => {
		const initialAssignTracksEdits: { [sessionUUID: string]: IUpdateSessionsTracks } = {};
		onDemandSessions?.forEach(session => {
			if (session) {
				initialAssignTracksEdits[session.uuid] = {
					tracks: session.tracks || [],
					sessionUUID: session.uuid,
				};
			}
		});
		setAssignTracksEdits(initialAssignTracksEdits);
	}, [onDemandSessions]);

	useEffect(() => {
		isOpen?.(showModal);
	}, [isOpen, showModal]);

	// Gets track list to populate track search dropdown
	const loadTracks = useCallback(async function() {
		try {
			if (activeChannel && token) {
				const tracks = await GetChannelTracks(activeChannel, token);
				setTrackDropdownList(tracks);
			}
		} catch (e) {
			console.error(e);
		}
	}, [token, activeChannel]);

	useEffect(() => {
		if (showModal && !trackDropdownList.length) {
			loadTracks();
		}
	}, [showModal, loadTracks, trackDropdownList.length]);

	const handleToggleShowAssignTracks = () => {
		setShowModal(prev => !prev);
	};

	const handleSave = () => {
		if (!token || !eventUUID) return;
		dispatch(updateSessionsTracks(token, eventUUID, assignTracksEdits));
		handleToggleShowAssignTracks();
	};

	if (!onDemandSessions) return null;
	return (
		<div>
			<button
				onClick={handleToggleShowAssignTracks}
				className="assign-tracks-trigger clear no-style cyan"
			>Assign tracks</button>

			<ModalComponent
				open={showModal}
				onRequestClose={handleToggleShowAssignTracks}
				title="Assign session tracks"
				cancellable={false}
				closeable={false}
				className="session-tracks-management-modal"
				footer={(
					<>
						<button
							onClick={handleToggleShowAssignTracks}
						>Cancel</button>
						<button
							className="primary"
							onClick={handleSave}
						>Save</button>
					</>
				)}
			>
				<div className="session-tracks-management-modal-content">
					{onDemandSessions.map(session => {
						if (!session) return null;
						return (
							<div key={session.uuid} className="session-tracks-management-card">
								<div>
									<TextInput
										defaultValue={session.title.base}
										placeholder=""
										label="Session name"
										className="session-tracks-management-card-title"
										disabled
									/>
								</div>
								<div>
									{/* This random button is so it steals auto focus and we don't auto focus the tag select input */}
									<button style={{ opacity: '0', position: 'absolute', zIndex: '-1' }}></button>
									<TagSelectInput
										label="Tracks"
										placeholder="Select tracks"
										className="session-tracks-management-card-tracks"
										onChange={tags => {
											setAssignTracksEdits(prev => ({
												...prev,
												[session.uuid]: {
													...prev[session.uuid],
													tracks: tags,
												}
											}));
										}}
										defaultTags={trackDropdownList}
										tags={assignTracksEdits?.[session.uuid]?.tracks || []}
										capSensitive
										noWhitespace
										maxTags={20}
										shouldSearch
										allowWithoutEnter={true}
									/>
								</div>
							</div>
						);
					})}
				</div>
			</ModalComponent>
		</div>
	);
};

export default AssignTracks;
